import { forceRefreshToken, getCurrentToken } from "api/token"
import { log } from "helpers/logger"

const CANCEL_REQUEST_TIMEOUT_MILLISECONDS = 10000

function parseFrameText(frameText) {
  const frame = { id: null, event: null, data: null }

  for (const line of frameText.split("\n")) {
    if (!line || line.startsWith(":")) continue
    const colon = line.indexOf(":")
    if (colon < 0) continue
    const field = line.slice(0, colon)
    const rawValue = line.slice(colon + 1)
    const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue
    if (field === "id") frame.id = value
    else if (field === "event") frame.event = value
    else if (field === "data") {
      frame.data = frame.data === null ? value : `${frame.data}\n${value}`
    }
  }

  return frame
}

export async function* parseSseFrames(readableStream) {
  const reader = readableStream.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let boundary = buffer.indexOf("\n\n")
      while (boundary !== -1) {
        const frame = parseFrameText(buffer.slice(0, boundary))
        buffer = buffer.slice(boundary + 2)
        if (frame.data !== null) yield frame
        boundary = buffer.indexOf("\n\n")
      }
    }
    if (buffer.length > 0) {
      const frame = parseFrameText(buffer)
      if (frame.data !== null) yield frame
    }
  } finally {
    reader.releaseLock?.()
  }
}

function parseDataPayload(frame) {
  try {
    return JSON.parse(frame.data)
  } catch {
    return null
  }
}

const TEXT_PART_ID = "assistant-text"

const TERMINAL_EVENT_TYPES = new Set(["final_message", "error", "cancelled"])

function toolCallIdFor(event) {
  return event.payload?.tool_call_id ?? event.id
}

function buildToolFinishedChunks(event) {
  const toolCallId = toolCallIdFor(event)
  const { error_type: errorType, trace_id: traceId, result, result_truncated: resultTruncated } = event.payload ?? {}
  if (!errorType) {
    const output = { status: "ok" }
    if (result != null) output.result = result
    if (resultTruncated != null) output.resultTruncated = resultTruncated
    return [{ type: "tool-output-available", toolCallId, output }]
  }
  const errorText = traceId ? `${errorType} (trace ${traceId})` : errorType
  return [{ type: "tool-output-error", toolCallId, errorText }]
}

function textPartIdFor(event) {
  return event.payload?.message_event_id ?? TEXT_PART_ID
}

export function createTextStreamState() {
  return { activeTextPartId: null }
}

function buildTextDeltaChunks(event, textStreamState) {
  const textPartId = textPartIdFor(event)
  const chunks = []
  if (textStreamState.activeTextPartId !== textPartId) {
    if (textStreamState.activeTextPartId != null) {
      chunks.push({ type: "text-end", id: textStreamState.activeTextPartId })
    }
    textStreamState.activeTextPartId = textPartId
    chunks.push({ type: "text-start", id: textPartId })
  }
  chunks.push({ type: "text-delta", id: textPartId, delta: event.payload?.delta ?? "" })
  return chunks
}

function mapFinalMessageArtifact(artifact) {
  if (artifact == null || typeof artifact !== "object") return null
  const { artifact_id: id, type } = artifact
  switch (type) {
    case "table": {
      const { records, columns, explain_steps: explainSteps } = artifact
      return { id, type, records, columns, explainSteps }
    }
    case "plot": {
      const { figure, table_artifact_id: tableArtifactId } = artifact
      return { id, type, figure, tableArtifactId }
    }
    case "text": {
      const { text } = artifact
      return { id, type, text }
    }
    default:
      return null
  }
}

function buildFinalMessageChunks(event, textStreamState) {
  const rawArtifacts = event.payload?.assistant_message?.artifacts
  const finishChunk = {
    type: "finish",
    messageMetadata: {
      threadId: event.thread_id,
      messageId: event.payload?.assistant_message?.message_id,
      text: event.payload?.assistant_message?.text,
      artifacts: (Array.isArray(rawArtifacts) ? rawArtifacts : []).map(mapFinalMessageArtifact).filter(Boolean),
    },
  }
  if (textStreamState.activeTextPartId != null) {
    return [{ type: "text-end", id: textStreamState.activeTextPartId }, finishChunk]
  }
  const text = event.payload?.assistant_message?.text ?? ""
  return [
    { type: "text-start", id: TEXT_PART_ID },
    { type: "text-delta", id: TEXT_PART_ID, delta: text },
    { type: "text-end", id: TEXT_PART_ID },
    finishChunk,
  ]
}

export function mapEventToChunks(event, textStreamState = createTextStreamState()) {
  switch (event.type) {
    case "tool_started":
      return [
        { type: "tool-input-start", toolCallId: toolCallIdFor(event), toolName: event.tool_name },
        { type: "tool-input-available", toolCallId: toolCallIdFor(event), toolName: event.tool_name, input: {} },
      ]
    case "tool_finished":
      return buildToolFinishedChunks(event)
    case "text_delta":
      return buildTextDeltaChunks(event, textStreamState)
    case "final_message":
      return buildFinalMessageChunks(event, textStreamState)
    case "error": {
      const { error_type: errorType, trace_id: traceId } = event.payload ?? {}
      const errorText = traceId ? `${errorType} (trace ${traceId})` : errorType
      return [{ type: "error", errorText }]
    }
    case "cancelled":
      return [{ type: "finish", finishReason: "abort", messageMetadata: { threadId: event.thread_id } }]
    case "turn_started":
    case "turn_finished":
    default:
      return []
  }
}

async function requestWithTokenRetry({ url, method, body, extraHeaders, abortSignal, account, userId, getNeedSignTerms }) {
  async function doFetch(token) {
    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "text/event-stream",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        "X-OmegaPoint-Code": token,
        ...extraHeaders,
      },
      ...(body !== undefined ? { body } : {}),
      signal: abortSignal,
    })
  }

  const token = await getCurrentToken({ account, userId })
  let response = await doFetch(token)
  if (response.status === 401 || response.status === 403) {
    if (getNeedSignTerms?.()) throw new Error("terms signature pending, skipping token refresh")
    const refreshedToken = await forceRefreshToken(account, userId)
    response = await doFetch(refreshedToken)
  }
  return response
}

export class StreamResumeError extends Error {
  constructor(kind, status) {
    super(`stream_resume_${kind}`)
    this.name = "StreamResumeError"
    this.kind = kind
    this.status = status
  }
}

async function pipeSseResponseToStream(response, controller, { onFrame, onEvent } = {}) {
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "")
    throw new Error(`upstream ${response.status}: ${text.slice(0, 200)}`)
  }
  const textStreamState = createTextStreamState()
  let sawTerminalEvent = false
  for await (const frame of parseSseFrames(response.body)) {
    onFrame?.(frame)
    const event = parseDataPayload(frame)
    if (!event) continue
    onEvent?.(event)
    if (TERMINAL_EVENT_TYPES.has(event.type)) sawTerminalEvent = true
    for (const chunk of mapEventToChunks(event, textStreamState)) {
      controller.enqueue(chunk)
    }
  }
  if (!sawTerminalEvent) {
    throw new Error("stream ended before a terminal event")
  }
}

export class ChatTransport {
  constructor({ accountAlias, account, userId, getNeedSignTerms } = {}) {
    this.accountAlias = accountAlias
    this.account = account
    this.userId = userId
    this._lastEventIdByChatId = new Map()
    this._streamIdByChatId = new Map()
    this._newThreadStreamId = null
    this.getNeedSignTerms = getNeedSignTerms
  }

  sendMessages({ chatId, body, abortSignal } = {}) {
    const url = chatId
      ? `/api/${this.accountAlias}/ai/chat/threads/${chatId}/stream`
      : `/api/${this.accountAlias}/ai/chat/threads/stream`

    return new ReadableStream({
      start: (controller) => this._streamRequest(controller, {
        url,
        method: "POST",
        body: JSON.stringify(body ?? {}),
        abortSignal,
        chatId,
        onFrame: (frame) => {
          if (chatId && frame.id != null) this._lastEventIdByChatId.set(chatId, frame.id)
        },
        onEvent: (event) => this._trackStreamId(chatId, event),
      }),
    })
  }

  async reconnectToStream({ chatId, abortSignal } = {}) {
    let streamId
    try {
      streamId = await this._resolveStreamId(chatId, abortSignal)
    } catch (error) {
      if (chatId != null && error != null && typeof error === "object") error.chatId = chatId
      this._streamIdByChatId.delete(chatId)
      this._lastEventIdByChatId.delete(chatId)
      throw error
    }
    if (streamId == null) return null

    const url = `/api/${this.accountAlias}/ai/chat/streams/${streamId}/resume`
    const lastEventId = this._lastEventIdByChatId.get(chatId)

    return new ReadableStream({
      start: (controller) => this._streamRequest(controller, {
        url,
        method: "GET",
        extraHeaders: lastEventId != null ? { "Last-Event-ID": lastEventId } : undefined,
        abortSignal,
        chatId,
        resumeChatId: chatId,
        checkResponse: (response) => {
          if (response.status === 404) throw new StreamResumeError("not_found", response.status)
          if (response.status === 410) throw new StreamResumeError("expired", response.status)
        },
        onFrame: (frame) => {
          if (frame.id != null) this._lastEventIdByChatId.set(chatId, frame.id)
        },
        onEvent: (event) => this._trackStreamId(chatId, event),
      }),
    })
  }

  _trackStreamId(chatId, event) {
    if (!chatId) {
      if (event?.stream_id != null) this._newThreadStreamId = event.stream_id
      if (TERMINAL_EVENT_TYPES.has(event?.type)) this._newThreadStreamId = null
      return
    }
    if (event?.stream_id != null) this._streamIdByChatId.set(chatId, event.stream_id)
    if (TERMINAL_EVENT_TYPES.has(event?.type)) {
      this._streamIdByChatId.delete(chatId)
      this._lastEventIdByChatId.delete(chatId)
    }
  }

  async cancelStream(chatId) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), CANCEL_REQUEST_TIMEOUT_MILLISECONDS)
    try {
      if (!chatId) {
        const newThreadStreamId = this._newThreadStreamId
        if (newThreadStreamId == null) return
        this._newThreadStreamId = null
        await this._deleteStream(newThreadStreamId, controller.signal)
        return
      }
      let streamId
      try {
        streamId = await this._resolveStreamId(chatId, controller.signal)
      } catch (error) {
        log(error)
        return
      }
      if (streamId == null) return
      this._streamIdByChatId.delete(chatId)
      this._lastEventIdByChatId.delete(chatId)
      await this._deleteStream(streamId, controller.signal)
    } finally {
      clearTimeout(timeoutId)
    }
  }

  async _deleteStream(streamId, abortSignal) {
    try {
      const response = await requestWithTokenRetry({
        url: `/api/${this.accountAlias}/ai/chat/streams/${streamId}`,
        method: "DELETE",
        abortSignal,
        account: this.account,
        userId: this.userId,
        getNeedSignTerms: this.getNeedSignTerms,
      })
      if (!response.ok) {
        log(new Error(`cancel stream ${streamId} returned ${response.status}`))
      }
    } catch (error) {
      log(error)
    }
  }

  async _resolveStreamId(chatId, abortSignal) {
    const trackedStreamId = this._streamIdByChatId.get(chatId)
    if (trackedStreamId != null) return trackedStreamId

    const url = `/api/${this.accountAlias}/ai/chat/threads/${chatId}/active-stream`
    const response = await requestWithTokenRetry({
      url,
      method: "GET",
      abortSignal,
      account: this.account,
      userId: this.userId,
      getNeedSignTerms: this.getNeedSignTerms,
    })
    if (response.status === 204) return null
    if (!response.ok) {
      const text = await response.text().catch(() => "")
      throw new Error(`upstream ${response.status}: ${text.slice(0, 200)}`)
    }

    const { streamId, lastEventId } = await response.json()
    this._streamIdByChatId.set(chatId, streamId)
    if (lastEventId != null && !this._lastEventIdByChatId.has(chatId)) {
      this._lastEventIdByChatId.set(chatId, lastEventId)
    }
    return streamId
  }

  async _streamRequest(controller, { url, method, body, extraHeaders, abortSignal, chatId, resumeChatId, checkResponse, onFrame, onEvent }) {
    try {
      const response = await requestWithTokenRetry({
        url,
        method,
        body,
        extraHeaders,
        abortSignal,
        account: this.account,
        userId: this.userId,
        getNeedSignTerms: this.getNeedSignTerms,
      })
      checkResponse?.(response)
      await pipeSseResponseToStream(response, controller, { onFrame, onEvent })
      controller.close()
    } catch (error) {
      if (error != null && typeof error === "object") error.chatId = chatId ?? null
      if (resumeChatId != null) {
        this._streamIdByChatId.delete(resumeChatId)
        this._lastEventIdByChatId.delete(resumeChatId)
      }
      controller.error(error)
    }
  }
}
